import { DateFormat, formatDateV2 } from '@agroone/dates'
import { ALLOWED_FILE_MIME_TYPE, ALLOWED_IMAGE_MIME_TYPE, Intervention, UserPermissions } from '@agroone/entities'

import { HttpClient } from '@angular/common/http'
import { Component, computed, inject, input, signal, Signal, output } from '@angular/core'
import { BndButton } from '@bonduelle-it/storybook'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { format } from 'date-fns'
import { IsGrantedDirective } from '../../directives'
import { API_URL } from '../../models/environment.token'
import { SharedConstantService, SharedUserService, ToastService } from '../../services'
import { getEffectiveDate } from '../../utils'
import { requireDefined } from '../../utils/require-defined.util'
import { FoodSafetyComponent } from './components/food-safety/food-safety.component'
import { IrrigationComponent } from './components/irrigation/irrigation.component'
import { NutrientsComponent } from './components/nutrients/nutrients.component'
import { OperationComponent } from './components/operation/operation.component'
import { PesticideComponent } from './components/pesticide/pesticide.component'
import { interventionTypes } from './constants/intervention.type'
import { InterventionApplicationsTypes } from './enums/intervention-applications-types.enum'
import { InterventionFoodSafetyTypes } from './enums/intervention-foodsafety-types.enum'
import { InterventionOperationsTypes } from './enums/intervention-operations-types.enum'
import { GroupedInterventionByName } from './models/grouped-intervention-by-name'
import { InterventionHistory } from './models/intervention-history.model'

@Component({
  selector: 'app-crop-intervention',
  templateUrl: './crop-intervention.component.html',
  styleUrls: ['./crop-intervention.component.scss'],
  imports: [
    TranslatePipe,
    FoodSafetyComponent,
    PesticideComponent,
    IrrigationComponent,
    NutrientsComponent,
    OperationComponent,
    BndButton,
    IsGrantedDirective,
  ],
})
export class CropInterventionComponent {
  readonly isReadOnly = input(true)
  readonly remove = output<Intervention>()
  readonly edit = output<Intervention>()
  readonly validate = output<Intervention>()

  private _apiUrl: string = inject(API_URL)

  public readonly PERMISSION = UserPermissions
  public interventionTypes = interventionTypes
  public interventionFoodSafetyTypes = InterventionFoodSafetyTypes
  public interventionApplicationsTypes = InterventionApplicationsTypes
  public interventionOperationsTypes = InterventionOperationsTypes
  public allowedFileMimeType = ALLOWED_FILE_MIME_TYPE
  public allowedImageMimeType = ALLOWED_IMAGE_MIME_TYPE

  public constantService = inject(SharedConstantService)
  public userService = inject(SharedUserService)
  private _http = inject(HttpClient)
  private _toastService = inject(ToastService)
  private translateService = inject(TranslateService)

  public readonly interventions = input<Intervention[] | undefined>([])
  public readonly interventionHistories: Signal<InterventionHistory[]> = computed(() =>
    this._buildHistory(this.interventions()),
  )

  /** Id of the intervention awaiting inline delete confirmation, or null when none. */
  public readonly pendingRemovalId = signal<number | null | undefined>(null)

  /** Id of the intervention awaiting inline validation confirmation, or null when none. */
  public readonly pendingValidationId = signal<number | null | undefined>(null)

  private _buildHistory(interventions: Intervention[] | undefined) {
    const histories: InterventionHistory[] = []
    // Guard against undefined/null interventions
    if (!interventions) {
      return histories
    }
    const grouped = this._sortCropInterventionsByDate(interventions, this.userService.currentUserDateFormat)

    for (const { date, interventions } of grouped) {
      const interventionHistory = new InterventionHistory(date)

      const groupedByUser = interventions.reduce(
        (acc, intervention: Intervention) => {
          const key = String(intervention.completedByName)
          if (!acc[key]) acc[key] = []
          acc[key].push(intervention)
          return acc
        },
        {} as Record<string, Intervention[]>,
      )

      for (const name of Object.keys(groupedByUser)) {
        const group = new GroupedInterventionByName(name)
        group.interventions = groupedByUser[name]
        interventionHistory.names.push(group)
      }

      interventionHistory.names.sort((x, y) => x.name.localeCompare(y.name))

      histories.push(interventionHistory)
    }

    return histories
  }

  private _sortCropInterventionsByDate(
    interventions: Intervention[],
    dateFormat: DateFormat | undefined,
  ): {
    date: string
    interventions: Intervention[]
  }[] {
    // Guard against undefined/null interventions
    if (!interventions || !Array.isArray(interventions)) {
      return []
    }

    const sortedActivities: Intervention[] = interventions
      .slice()
      .sort((a, b) => new Date(getEffectiveDate(b) ?? 0).getTime() - new Date(getEffectiveDate(a) ?? 0).getTime())

    const groupedMap: Map<string, Intervention[]> = new Map<string, Intervention[]>()

    for (const activity of sortedActivities) {
      const appliedDate: Date = new Date(getEffectiveDate(activity) ?? 0)
      // date-fns stringifies the format, a missing one included
      const formattedDay: string = format(appliedDate, String(dateFormat))
      let dayActivities = groupedMap.get(formattedDay)
      if (!dayActivities) {
        dayActivities = []
        groupedMap.set(formattedDay, dayActivities)
      }
      dayActivities.push(activity)
    }

    return Array.from(groupedMap.entries()).map(([date, interventions]: [string, Intervention[]]) => ({
      date,
      interventions,
    }))
  }

  public removeIntervention(intervention: Intervention) {
    this.pendingRemovalId.set(intervention.id)
  }

  public confirmRemoveIntervention(intervention: Intervention) {
    this.pendingRemovalId.set(null)
    this.remove.emit(intervention)
  }

  public cancelRemoveIntervention() {
    this.pendingRemovalId.set(null)
  }

  public editIntervention(intervention: Intervention) {
    this.edit.emit(intervention)
  }

  public validateIntervention(intervention: Intervention) {
    this.pendingValidationId.set(intervention.id)
  }

  public confirmValidateIntervention(intervention: Intervention) {
    this.pendingValidationId.set(null)
    this.validate.emit(intervention)
  }

  public cancelValidateIntervention() {
    this.pendingValidationId.set(null)
  }

  public getInterventionItemTestId(intervention: Intervention): string {
    const raw = intervention?.subType ?? 'unknown'
    const slug = String(raw)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]/g, '-')
      .replace(/-+/g, '-')
    return `intervention-item-${slug}`
  }

  public getValidatorTitle(intervention: Intervention): string {
    if (!intervention?.validatedDate) return ''

    const time = formatDateV2(new Date(intervention.validatedDate), DateFormat.HH_MM)
    const validatedDate = `${formatDateV2(
      new Date(intervention.validatedDate),
      this.userService.currentUserDateFormat as unknown as DateFormat,
    )} ${time}`

    const first = intervention?.validatedByFirstname
    const last = intervention?.validatedByLastname
    if (first && last) {
      return this.translateService.instant('INTERVENTION.VALIDATION_TITLE', {
        validatorName: `${first} ${last}`,
        validatedDate,
      })
    }
    return ''
  }

  public openFile(interventionId: number | undefined, documentId: number | undefined): void {
    const interventionDocumentUrl = `${this._apiUrl}/documents/interventions`
    const interventionPath = requireDefined(interventionId, 'the intervention id')
    const documentPath = requireDefined(documentId, 'the document id')

    this._http
      .get(`${interventionDocumentUrl}/${interventionPath}/documents/${documentPath}`, {
        responseType: 'text',
      })
      .subscribe({
        next: (signedUrl) => {
          if (signedUrl) window.open(signedUrl, '_blank')
        },
        error: () => this._toastService.showToastError('An error occured'),
      })
  }
}
